import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { Typography, Button, AlertColor, Divider, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { CollectorInstallError, GroupTypeDetails } from 'graphql/types';
import { EnabledDisabled } from 'modules/Settings/constant';
import { useScrollbarStyles } from 'modules/common/styles';
import { useSidePanelContext } from 'modules/rootPage';
import { useMessage } from 'modules/intl';
import { CollectorErrorListUnified } from '../CollectorErrorListUnified';
import { ToastSnackBar } from '../../../Settings/ToastSnackBar';
import { CollectorId } from '../types';
import { GroupsResult } from './hooks';
import { DefaultSettingsRow } from './DefaultSettingsRow';
import { SettingsRow } from './SettingsRow';
import {
  CollectorRowSettings,
  FormData,
  DesktopCollectorFormData
} from './hooks';
import { EnabledDisabledDropdown } from './EnabledDisabledDropdown';
import InvalidInputError from './InvalidInputError';

type CollectorSettingsBodyProps = {
  collectorId?: string;
  enabledGroupTypes: GroupTypeDetails[];
  defaultEnabled?: EnabledDisabled;
  errors?: Array<CollectorInstallError | null | undefined> | null;
  defaultOptedIn?: EnabledDisabled;
  defaultAppTrackingStatus?: EnabledDisabled;
  prepend: UseFieldArrayReturn['prepend'];
  remove: UseFieldArrayReturn['remove'];
  handleSubmit: UseFormReturn['handleSubmit'];
  loading?: boolean;
  fields: Record<string, any>[];
  fieldName: string;
  onSave?: (note: FormData) => Promise<void>;
  onUserCollectorSave?: (note: DesktopCollectorFormData) => Promise<void>;
  installId?: string;
  retryUrl?: string;
  isDirty: boolean;
  hasInvalidInput?: boolean;
};

type StyleProps = {
  rows: number;
};

const useStyles = makeStyles((theme) => ({
  actions: {
    justifyContent: 'flex-end',
    display: 'flex',
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(2.5),
    width: '100%'
  },
  actionButton: {
    margin: theme.spacing(3, 0, 0, 0)
  },
  footer: {
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'column',
    height: ({ rows }: StyleProps) => (rows ? 'auto' : '100%')
  },
  spacer: {
    flexGrow: 1,
    flexShrink: 1
  },
  settingsRowContent: {
    marginTop: theme.spacing(2),
    alignContent: 'flex-start',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 300px)',
    width: '100%'
  },
  title: {
    margin: theme.spacing(1.75, 0, 0, 0),
    color: theme.palette.text.secondary
  },
  errorWidth: {
    width: '98%'
  },
  hzDivider: {
    width: '100%'
  },
  invalidInputError: {
    color: '#587CA5'
  },
  errorBanner: {
    backgroundColor: '#DEEEFF',
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    width: 'fit-content',
    marginTop: theme.spacing(3)
  }
}));

export const CollectorSettingsBody: React.FC<CollectorSettingsBodyProps> = ({
  collectorId,
  loading,
  enabledGroupTypes,
  defaultAppTrackingStatus,
  onUserCollectorSave,
  defaultEnabled,
  defaultOptedIn,
  prepend,
  errors,
  remove,
  fieldName,
  fields,
  onSave,
  handleSubmit,
  installId,
  retryUrl,
  isDirty,
  hasInvalidInput
}: CollectorSettingsBodyProps) => {
  const classesScrollbar = useScrollbarStyles();
  const classes = useStyles({
    rows: fields.length
  });
  const { pinned } = useSidePanelContext();
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    if (enabledGroupTypes && enabledGroupTypes.length) {
      const values = enabledGroupTypes.reduce((a, c) => {
        a[c.value] = [];
        return a;
      }, {} as GroupsResult);
      prepend({
        ...values,
        ...{
          enableDisabled: defaultEnabled,
          optedIn: defaultOptedIn
        }
      });
    }
  }, [enabledGroupTypes, prepend, defaultEnabled, defaultOptedIn]);

  const handleRemove = useCallback(
    (rowId) => {
      remove(rowId);
    },
    [remove]
  );
  const isDesktopCollector = useMemo(
    () => Boolean(collectorId && collectorId === CollectorId.DesktopCollector),
    [collectorId]
  );

  const onSaveClick = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    await handleSubmit(isDesktopCollector ? onUserCollectorSave : onSave)();
    setIsSnackBarOpen(true);
  }, [handleSubmit, onSave, isDesktopCollector, onUserCollectorSave]);

  const collectorSaveSuccessMessage = useMessage(
    'collectorSetting.collectorSaveSuccessMessage'
  );
  const category = 'success' as AlertColor;
  return (
    <div className={classes.rightContainer}>
      {installId && (
        <div className={classes.errorWidth}>
          <CollectorErrorListUnified
            data-testid="collectorDataList-testid"
            errors={errors}
            retryURL={retryUrl || ''}
            installId={installId}
          />
        </div>
      )}
      {isDesktopCollector && (
        <Stack gap={3} alignItems={'flex-start'}>
          <Typography variant="h6" className={classes.title}>
            <FormattedMessage id="collectorSetting.appTrackingSettings" />
          </Typography>
          <EnabledDisabledDropdown
            value={defaultAppTrackingStatus}
            rowID={'appTrackingDefaultRow'}
            labelTextId={'collectorSetting.appTracking'}
          />
          <Divider
            className={classes.hzDivider}
            orientation="horizontal"
            data-testid="collectorImgDivider"
          />
        </Stack>
      )}
      <Typography
        variant="h6"
        color={'textSecondary'}
        className={classes.title}
      >
        <FormattedMessage
          id={
            isDesktopCollector
              ? 'collectorSetting.screentimeSettings'
              : 'collectorSetting.companyCollectorTitle'
          }
        />
      </Typography>

      <div
        className={classnames(
          classes.settingsRowContent,
          classesScrollbar.scrollbar
        )}
        data-testid="setting-body"
      >
        {Boolean(enabledGroupTypes.length) &&
          fields.map((field, index) => (
            <SettingsRow
              key={field.id}
              rowID={`${fieldName}.${index}`}
              rowIndex={index}
              isDesktopCollector={isDesktopCollector}
              field={field as CollectorRowSettings}
              onRemove={handleRemove}
              enabledGroupTypes={enabledGroupTypes}
            />
          ))}
      </div>

      <DefaultSettingsRow
        isDesktopCollector={isDesktopCollector}
        defaultEnabled={defaultEnabled}
        defaultOptedIn={defaultOptedIn}
        handleClick={handleClick}
        disabled={!enabledGroupTypes.length}
      />
      <div className={classes.spacer} />
      {hasInvalidInput && <InvalidInputError />}
      <div>
        <Button
          className={classes.actionButton}
          disabled={loading || !isDirty || hasInvalidInput}
          color="primary"
          variant="contained"
          onClick={onSaveClick}
          data-testid="save-button"
        >
          <FormattedMessage id="collectorSetting.save" />
        </Button>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={category}
          message={collectorSaveSuccessMessage}
          onClose={onSnackbarClose}
          pinned={pinned as boolean}
        />
      </div>
    </div>
  );
};
